import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect } from 'react';

import { LegacyResponseApplicationError } from '@verantos-dev/types-common';

import { getCancelButtonLabel, getSaveButtonLabel } from './buttonHelpers';
import { ApiWorkflowDialogSteps } from './enums';
import type { ApiWorkflowDialogMessages } from './types';
import type { DialogButton, DialogSaveButton } from '../../types';

export interface ApiWorkflowDialogButtonsProps {
  currentStep: ApiWorkflowDialogSteps;
  isValidForSubmit: boolean;
  isInitialLoading: boolean;
  initialLoadingError: FetchBaseQueryError | SerializedError | undefined;
  isSubmitting: boolean;
  submittingError:
    | LegacyResponseApplicationError[]
    | FetchBaseQueryError
    | SerializedError
    | undefined;
  dialogMessages?: ApiWorkflowDialogMessages;
  saveButtonColor?: DialogButton['color'];
  onClose?: VoidFunction;
  onConfirm?: VoidFunction;
  onNotConfirm?: VoidFunction;
  onSubmit?: VoidFunction;
  onRedirectAfterSubmit?: VoidFunction;
  onRetryInitialLoadingFetch?: VoidFunction;
  onRetrySubmittingFetch?: VoidFunction;
  successCountdownTime?: number;
}

export const useApiWorkflowDialogButtons = ({
  currentStep,
  initialLoadingError,
  isInitialLoading,
  isSubmitting,
  submittingError,
  isValidForSubmit,
  saveButtonColor = 'primary',
  onClose,
  onConfirm,
  onNotConfirm,
  onSubmit,
  onRedirectAfterSubmit,
  onRetryInitialLoadingFetch,
  onRetrySubmittingFetch,
  successCountdownTime,
  dialogMessages,
}: ApiWorkflowDialogButtonsProps) => {
  const onSaveButtonClick = () => {
    if (currentStep === ApiWorkflowDialogSteps.UserEntry) {
      // calls retry function if on entry step and there is an error
      if (initialLoadingError) {
        onRetryInitialLoadingFetch ? onRetryInitialLoadingFetch() : onClose && onClose();
        return;
      }

      // calls submit function if on entry step
      onSubmit && onSubmit();
    } else if (currentStep === ApiWorkflowDialogSteps.UserConfirmation) {
      // calls confirm function if on confirmation step
      onConfirm && onConfirm();
    } else if (currentStep === ApiWorkflowDialogSteps.UserSubmitted) {
      // calls retry function if on submitted step and there is an error
      if (submittingError) {
        onRetrySubmittingFetch ? onRetrySubmittingFetch() : onClose && onClose();
        return;
      }

      // calls redirect function if on submitted step, otherwise close
      onRedirectAfterSubmit ? onRedirectAfterSubmit() : onClose && onClose();
    }
  };

  const saveButtonLabel = getSaveButtonLabel({
    currentStep,
    initialLoadingError,
    isSubmitting,
    submittingError,
    onRetryInitialLoadingFetch,
    onRetrySubmittingFetch,
    dialogMessages,
    isInitialLoading,
  });

  const isSaveButtonDisable =
    isInitialLoading || // disabled while initial loading
    isSubmitting || // disabled while submitting
    (!initialLoadingError && !isValidForSubmit); // disabled if not valid for submit

  const saveButton: DialogSaveButton = {
    label: saveButtonLabel,
    onClick: onSaveButtonClick,
    disabled: isSaveButtonDisable,
    color: saveButtonColor,
    startIcon: undefined,
    wait: isSubmitting,
    countdown:
      currentStep === ApiWorkflowDialogSteps.UserSubmitted && // countdown only on submitted step
      !isSubmitting && // countdown only when not submitting
      !(initialLoadingError || submittingError), // countdown only when no error
    countdownTime: successCountdownTime || 3, // countdown time in seconds
  };

  const onCancelButtonClick = () => {
    if (currentStep === ApiWorkflowDialogSteps.UserEntry) {
      // calls close function if on entry step
      onClose && onClose();
    } else if (currentStep === ApiWorkflowDialogSteps.UserConfirmation) {
      // calls not confirm function if on confirmation step which will go back to entry step
      onNotConfirm && onNotConfirm();
    }
  };

  const cancelButtonLabel = getCancelButtonLabel({
    currentStep,
    isSubmitting,
    dialogMessages,
    isInitialLoading,
    initialLoadingError,
    submittingError,
  });

  const cancelButton: DialogButton = {
    label: cancelButtonLabel,
    onClick: onCancelButtonClick,
    disabled: isSubmitting,
    color: 'text',
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle enter case
      if (event.key === 'Enter' && !isSaveButtonDisable) {
        onSaveButtonClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSaveButtonDisable, onSaveButtonClick]);

  return {
    saveButton,
    cancelButton: currentStep === ApiWorkflowDialogSteps.UserSubmitted ? undefined : cancelButton,
  };
};
